/**
 * Vue directive for element IntersectionObserver
 *
 * template:
 * <div v-observe-intersection @exposed="onExposed" @intersection-changed="onIntersectionChange"></div>
 *
 * script:
 * import IODirective from '@/directives/intersection-observe.directive';
 * Vue.directive('observe-intersection', IODirective);
 *
 * function onExposed() {
 *   // short cut for onIntersectionChange: event.intersectionObserverEntry.isIntersecting
 * }
 * function onIntersectionChange(event: CustomEvent) {
 *   console.log(event.intersectionObserverEntry);
 * }
 */
import './intersection-observer-polyfill';

const map = new Map();

function unbind(el) {
    const intersectionObserver = map.get(el);
    if (intersectionObserver) {
        intersectionObserver.unobserve(el);
        map.delete(el);
    }
}

function getCallback(el, options = {}) {
    return entries => {
        const entry = entries[0];
        if (!entry) {
            return;
        }
        if (entry.isIntersecting) {
            const exposedEvent = new CustomEvent('exposed');
            el.dispatchEvent(exposedEvent);
            if (options.exposeOnce) {
                unbind(el);
            }
        } else {
            const exposedEvent = new CustomEvent('disposed');
            el.dispatchEvent(exposedEvent);
        }
        const IOEvent = new CustomEvent('intersection-changed');
        IOEvent.intersectionObserverEntry = entry;
        el.dispatchEvent(IOEvent);
    };
}

async function bind(el, binding, vnode) {
    if (vnode.context) {
        await vnode.context.$nextTick();
    }
    const options = { ...binding.value };
    const intersectionObserver = new IntersectionObserver(getCallback(el, { ...binding.value }), options);
    intersectionObserver.observe(el);
    map.set(el, intersectionObserver);
}

const IntersectionObserveDirective = {
    bind,
    unbind,
};

export default IntersectionObserveDirective;
